import 'whatwg-fetch';
import Cookies from 'js-cookie';
import _ from 'lodash';
import * as queryStringHelpers from 'qs';

import { handleResponseStatus } from './APIResponseHandler';
import * as LendingAPIProviderFactory from './LendingAPIProviderFactory';
import { serializeApplicationState } from '../helpers/applicationManager';
import { getPathFromUrl, serializeQuery } from '../helpers/helpers';
import { redirectToURL } from '../helpers/redirection';
import * as IwDataDogLogger from '../loggers/IwDataDogLogger';

//TODO: this is a temporary solution because there is no endpoint for now
var temporaryStateKey;

export function getStateKey() {
    return temporaryStateKey;
}

export function setTemporaryStateKey(stateKey) {
    temporaryStateKey = stateKey;
}

var partnerData;

export function handleError(response) {
    const errorInformation = {
        response,
        severity: 'error'
    };
    logError(errorInformation);
}

export function handleFatalError(response) {
    const errorInformation = {
        response,
        severity: 'fatal'
    };
    logError(errorInformation);
}

export function waitForErrorToLogThenRedirectToLogin(response) {
    handleError(response);

    setTimeout(() => {
        redirectToURL('/login/');
    }, 200);
}

function logError(errorInformation) {
    const message = `${errorInformation.response.status} ${getPathFromUrl(
        errorInformation.response.url
    )}`;

    const logData = {
        level: errorInformation.severity,
        message: message,
        extra: {
            status: errorInformation.response.status,
            statusText: errorInformation.response.statusText,
            url: errorInformation.response.url
        }
    };

    // This response payload parsing is quite important, as it:
    // - Makes it available in DataDog for debugging purposes...
    // - ...but also available in browsers dev tools, where the response payload was previously rendered empty, even
    // if filled with server error description.
    errorInformation.response
        .json()
        .then(function (payload) {
            logData.extra.payload = payload;

            IwDataDogLogger.logError({
                message,
                tags: {
                    reporter: 'api'
                },
                extra: {
                    isJson: true,
                    status: errorInformation.response.status,
                    statusText: errorInformation.response.statusText,
                    url: errorInformation.response.url
                }
            });
        })
        .catch(function (error) {
            logData.extra.loggingError = error;

            IwDataDogLogger.logError({
                message,
                tags: {
                    reporter: 'api'
                },
                extra: {
                    status: errorInformation.response.status,
                    statusText: errorInformation.response.statusText,
                    url: errorInformation.response.url
                }
            });
        });
}

export function getInitialApplicationStateFetcher({ stateKey }) {
    return authenticatedFetchJSON(`/api/lending/edge/state/${stateKey}/`)
        .then((state) => {
            temporaryStateKey = state.data.state_key;

            if (state.data && state.data.partner) {
                partnerData = state.data.partner;
            }

            return state.data;
        })
        .then((stateData) => {
            const apiProvider =
                LendingAPIProviderFactory.getInstance(countryCode);
            return { data: apiProvider.deserialiseState(stateData) };
        });
}

export function fetchCompaniesLookup(
    companyName,
    postcode,
    apiExtractorFunction
) {
    if (!companyName) return [];
    let searchParameters = { company_name: companyName, postcode };
    return runApiWithSearchParameters(
        '/api/lending/edge/company_search',
        searchParameters
    )
        .then(({ data }) => data.map(apiExtractorFunction))
        .catch(() => []);
}

export function extractValuesFromEnglishCompanySearchAPIResponse(company) {
    return {
        ...mapEnglishCompanySearchAPIResponseToIwocaSelectFormat(company),
        address: company.address || null,
        registration_date: company.registration_date || null,
        company_type: company.company_type || null
    };
}

export const extractValuesFromGermanCompanySearchApiResponse = (company) => ({
    ...mapGermanCompanySearchAPIResponseToIwocaSelectFormat(company),
    name: company.name,
    crefo_id: company.crefo_id,
    town: company.city,
    postcode: company.postcode,
    street_line_1: company.street,
    building_number: company.house_number ? company.house_number.toString() : ''
});
function mapEnglishCompanySearchAPIResponseToIwocaSelectFormat(company) {
    return {
        value: company.number,
        label: company.name
    };
}

function mapGermanCompanySearchAPIResponseToIwocaSelectFormat(company) {
    return {
        value: company.city,
        label: company.name
    };
}

export const fetchLegalForm = (crefoId) => {
    if (!crefoId) return '';
    const searchParameters = { crefo_id: crefoId };
    return runApiWithSearchParameters(
        '/api/lending/edge/legal_form',
        searchParameters
    )
        .then((response) => response.data.legal_form)
        .catch(() => '');
};

const runApiWithSearchParameters = (api, parameters) => {
    const trimmedSearchParameters = _.mapValues(parameters, _.trim);
    const cleanedSearchParameters = _.pickBy(
        trimmedSearchParameters,
        (v) => !_.isEmpty(v)
    );
    const constructedQueryStringParameters = queryStringHelpers.stringify(
        cleanedSearchParameters
    );
    return authenticatedFetchJSON(
        `${api}/?${constructedQueryStringParameters}`
    );
};

export function fetchAddressLookup(postcode) {
    return authenticatedFetchJSON(
        `/api/lending/edge/postcode_lookup/?postcode=${postcode}`
    )
        .then(({ data }) => {
            return data.map(({ ptc_abs_code, ...address }) => ({
                equifax_token: ptc_abs_code,
                ...address
            }));
        })
        .catch(() => {
            return [];
        });
}

/**
 * Ideally should be moved to containers, to be send in an action payload with the state
 */
let countryCode;

export function setCountryCodeSingleton(activeCountryCode) {
    countryCode = activeCountryCode;
}

export const fetchAssociatedPeople = (stateKey) => {
    return authenticatedFetchJSON(
        `/api/lending/edge/associated_people/${stateKey}/`
    ).then((response) => response.data.people);
};

export const CONTEXT_URL = '/api/lending/edge/context/';
export function fetchContext() {
    return authenticatedFetchJSON(CONTEXT_URL).then(
        (response) => response.data
    );
}

export function fetchAdditionalGuarantorStatus() {
    return authenticatedFetchJSON(
        '/automated_lending/check_additional_guarantors/'
    );
}

export function putNewState(data, storeCountryCode) {
    countryCode = storeCountryCode || countryCode;
    return serializeApplicationState(data.data.application, countryCode)
        .then((application) => {
            const omitUISteps = ({ ...ui }) => ({ ...ui });
            const ui = omitUISteps(data.data.ui);
            return _.assign(
                {},
                { data },
                {
                    data: { ui, application }
                }
            );
        })
        .then((state) => {
            var data = getDataToSerialize(state, countryCode);
            var fetcher = getSaveStateFetcher(data);

            return fetcher;
        });
}

function getSaveStateFetcher(data) {
    return authenticatedFetchJSON(
        `/api/lending/edge/state/${temporaryStateKey}/`,
        {
            method: 'PUT',
            headers: {
                'X-CSRFToken': Cookies.get('csrftoken'),
                'Content-Type': 'application/json',
                Accept: 'application/json'
            },
            body: JSON.stringify(data)
        }
    );
}

export function fetchIntercomAttributes() {
    const params = {
        filters: [
            {
                key: 'user_id',
                value: 'CURRENT_USER'
            }
        ],
        fields: ['email', 'email_hashed', 'display_widget']
    };
    const query = serializeQuery(params);

    return authenticatedFetchJSON(
        `/api/v3/intercom_account_customer/?${query}`
    ).then((response) => response.data[0]);
}

export function linkAccount(options) {
    const { url, data, redirect } = options;

    return authenticatedFetchJSON(url, {
        method: 'POST',
        headers: {
            'X-CSRFToken': Cookies.get('csrftoken'),
            'Content-Type': 'application/json',
            Accept: 'application/json'
        },
        body: JSON.stringify(data)
    }).then((response) => {
        if (redirect) {
            window.location = response.url;
        }
        return response;
    });
}

function getDataToSerialize(state, storeCountryCode) {
    const apiProvider = LendingAPIProviderFactory.getInstance(storeCountryCode);
    let stateData = state.data;

    if (partnerData) {
        stateData.partner = partnerData;
    }

    return { data: apiProvider.serialiseState(stateData) };
}

export function authenticatedPostFetch(url, options = {}) {
    options.method = 'POST';
    return authenticatedFetch(url, options);
}

export function authenticatedPostFetchJSON(url, options = {}) {
    options.method = 'POST';

    let fetchPromise = authenticatedFetch(url, options);
    return fetchPromise.then((response) => response.json());
}

export function authenticatedPutFetch(url, options) {
    options.method = 'PUT';
    return authenticatedFetch(url, options);
}

export function authenticatedFetchJSON(url, options) {
    let fetchPromise = authenticatedFetch(url, options);

    return fetchPromise.then((response) => response.json());
}

export const getOptions = (options = {}) => {
    options.credentials = 'same-origin';
    options.headers = {
        'X-CSRFToken': Cookies.get('csrftoken'),
        'Content-Type': 'application/json',
        Accept: 'application/json'
    };

    return options;
};

function authenticatedFetch(url, optionOverrides) {
    return fetch(url, getOptions(optionOverrides)).then(handleResponseStatus);
}
